/* =========================================
   Product Preview Overlay — eisNova
   
   Web Component <eisnova-product-overlay> that slides in on top of the
   cart drawer when the user clicks a trigger with [data-eisnova-overlay-open].
   Fetches product JSON on demand, populates the template, handles variant
   selection, and submits to /cart/add.js with sections piggyback so the
   drawer re-renders without a separate refresh (matching Phase A pattern).

   TRIGGER CONTRACT:
   <button data-eisnova-overlay-open data-eisnova-product-handle="foo-bar">
     Quick shop
   </button>

   This file also owns:
   - data-eisnova-add-variant delegation (direct single-variant add)
   - data-eisnova-close-drawer delegation (close cart drawer)
   - data-eisnova-toggle / data-eisnova-close helpers for cross-sell panel
   - Discount code form wiring for .eisNova-promo (replaces old inline onclick)
========================================= */

"use strict";

/* =========================================
   Overlay Web Component
========================================= */
class EisNovaProductOverlay extends HTMLElement {
  constructor() {
    super();
    this._product = null;
    this._selectedVariantId = null;
    this._qty = 1;
    this._MAX_QTY = 4; /* matches live theme's cart-drawer-product quantity max */
    this._lastFocus = null;
  }

  connectedCallback() {
    /* Event delegation for overlay-local controls. All buttons have
       data-attributes so we don't rely on brittle class selectors. */
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-eisnova-overlay-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.target.closest('[data-eisnova-overlay-qty-plus]')) {
        this._changeQty(+1);
        return;
      }
      if (e.target.closest('[data-eisnova-overlay-qty-minus]')) {
        this._changeQty(-1);
        return;
      }
      if (e.target.closest('[data-eisnova-overlay-submit]')) {
        this._submitAdd();
        return;
      }
      const variantBtn = e.target.closest('[data-eisnova-variant-id]');
      if (variantBtn) {
        this._selectVariant(variantBtn.dataset.eisnovaVariantId);
        return;
      }
    });

    this.addEventListener('input', (e) => {
      if (e.target.matches('[data-eisnova-overlay-qty-input]')) {
        this._setQty(parseInt(e.target.value, 10) || 1);
      }
    });

    /* Esc closes overlay — scoped to when overlay is open */
    this._escHandler = (e) => {
      if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    };
    document.addEventListener('keydown', this._escHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._escHandler);
  }

  /* ---------- PUBLIC API ---------- */

  /*
    Open overlay. PRE-RENDERED PAYLOAD STRATEGY:
    
    Instead of fetching /products/{handle}.js (which doesn't return metafields,
    has wrong-sized images, and returns options as objects) we read the full
    product payload from a Liquid-rendered JSON script tag:
    
      <script type="application/json" data-eisnova-overlay-product="{handle}">
        {id, title, type, description, url, optionName, tags, images, variants[]}
      </script>
    
    Each variant in the payload carries:
      - id, title, available
      - price (cents), priceFormatted (Liquid-rendered "44.50 €")
      - image (1200w sized URL)
      - colorRgb (metafield, makeup only)
      - packSize (metafield, perfumes)
    
    Falls back to /products/{handle}.js fetch ONLY if the payload script
    is missing for some reason (legacy markup, third-party injection).
  */
  async open(handle) {
    if (!handle) return;
    this._lastFocus = document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    this.removeAttribute('data-eisnova-overlay-state');
    this._showError('');

    try {
      if (!this._cache) this._cache = new Map();
      let product = this._cache.get(handle);

      if (!product) {
        /* Primary path: read pre-rendered Liquid payload */
        const payloadEl = document.querySelector(
          `script[data-eisnova-overlay-product="${handle}"]`
        );
        if (payloadEl) {
          try {
            product = JSON.parse(payloadEl.textContent);
          } catch (e) {
            console.warn('[eisNova-overlay] payload parse failed, falling back to API:', e);
          }
        }

        /* Fallback: legacy /products/{handle}.js fetch */
        if (!product) {
          const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
          if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
          product = await res.json();
          /* Normalize options shape — /products.js returns array of objects;
             our pre-rendered payload uses optionName as plain string. */
          product.optionName = product.options?.[0]?.name || product.options?.[0] || 'Variant';
        }

        this._cache.set(handle, product);
      }

      this._product = product;
      this._qty = 1;
      const firstAvail = product.variants.find(v => v.available) || product.variants[0];
      this._selectedVariantId = firstAvail ? String(firstAvail.id) : null;
      this._render();
      this.setAttribute('data-eisnova-overlay-state', 'ready');

      requestAnimationFrame(() => {
        const firstBtn = this.querySelector('.eisNova-overlay__variation, [data-eisnova-overlay-submit]');
        if (firstBtn) firstBtn.focus();
      });
    } catch (err) {
      console.error('[eisNova-overlay] open failed:', err);
      this._showError('Could not load product.');
    }
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    /* Return focus to trigger element for a11y */
    if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
      this._lastFocus.focus();
    }
  }

  /* ---------- RENDERING ---------- */

  /*
    Initial render after open(). Populates all overlay fields from the
    pre-rendered Liquid product payload.

    Image gallery uses the SELECTED variant's image when available, so
    makeup overlay opens directly on the right shade visual — no flash
    of "default" image then swap.
  */
  _render() {
    const p = this._product;
    if (!p) return;

    this._setText('title', p.title);
    this._setText('category', p.type || '');
    this._setText('description', this._plainDescription(p.description));
    this._setHref('discoverMoreUrl', p.url);

    /* Find selected variant from this._selectedVariantId set in open() */
    const selectedVariant = p.variants.find(v => String(v.id) === this._selectedVariantId);

    /* Pack size from variant metafield — falls back to empty string */
    this._setText('packSize', selectedVariant?.packSize || '');

    this._renderImages(p, selectedVariant);
    this._renderVariations(p);
    this._syncSelectedVariantUI();
    this._syncQtyUI();
  }

  /*
    Renders product gallery in the overlay panel.

    Strategy:
      - For makeup (variant has its own .image): show variant.image FIRST,
        then any other product.images that aren't the same URL.
        This makes the gallery responsive to shade selection while still
        showing extra brand/macro photography from product.images.
      - For everything else: show full product.images[] gallery.

    URLs come pre-sized from Liquid at 1200w (image_url filter). No URL
    rewriting needed — the previous regex `_x550` substitution didn't even
    match Shopify CDN URLs (no .ext in the path) so it was a no-op anyway.
  */

/*
    Normalizes the colorRgb metafield value to a CSS-valid color string.

    Shopify metafields can serialize "color" data in several ways depending
    on the metafield TYPE configured in Admin:
      - 'single_line_text' with hand-typed "rgb(190,30,30)"
        → JSON gives plain string  "rgb(190,30,30)"
      - 'color' (Shopify 2.0 typed metafield)
        → JSON gives string "#BE1E1E" (Shopify converts internally)
      - Drops sometimes appear as object {red, green, blue, alpha}
        when Liquid `| json` runs on a typed metafield drop directly.
    
    This helper returns a CSS-usable string for any of the above shapes,
    or null if the input is malformed.
  */
  /*
    Normalizes the colorRgb metafield value to a CSS-valid color string.
    Handles every Liquid serialization shape we've seen:
      - plain string "rgb(...)"
      - hex string "#BE1E1E"
      - {red, green, blue, alpha} (color metafield drop with `value` key)
      - {rgb: "..."} / {hex: "..."} / {value: "..."} wrappers
      - nested .value access (Shopify 2.0 typed metafield)
    
    Logs to console.warn for any unknown shape so we can extend this
    method as new metafield serializations show up. Returns null when
    nothing usable is found.
  */
  _normalizeColor(input) {
    if (!input) return null;

    /* Plain string — already usable */
    if (typeof input === 'string') {
      const trimmed = input.trim();
      return trimmed || null;
    }

    /* Object shapes */
    if (typeof input === 'object') {
      /* Direct {red, green, blue, alpha} */
      if ('red' in input && 'green' in input && 'blue' in input) {
        const a = (typeof input.alpha === 'number') ? input.alpha : 1;
        return `rgba(${input.red}, ${input.green}, ${input.blue}, ${a})`;
      }
      /* Wrapper shapes */
      if (input.rgb) return String(input.rgb);
      if (input.hex) return String(input.hex);
      /* Shopify 2.0 typed metafield drop: {type, value} */
      if (input.value) {
        return this._normalizeColor(input.value); // recurse — value may be string or object
      }
      /* Shopify 2.0 list shape sometimes: array of one item */
      if (Array.isArray(input) && input.length > 0) {
        return this._normalizeColor(input[0]);
      }

      /* Unknown shape — log it once so we can extend this list. */
      if (!this._unknownColorLogged) {
        this._unknownColorLogged = true;
        console.warn('[eisNova-overlay] Unknown colorRgb shape:', input,
          'Add a handler in _normalizeColor() for keys:', Object.keys(input));
      }
    }
    return null;
  }

 /*
    Renders product gallery in the overlay panel.

    SOURCE PRIORITY (matches legacy cart-drawer-product behavior):
      1. selectedVariant.media[] — from variant metafield global.var_images.
         Makeup variants typically have 3-5 images each (different angles,
         shades on skin, textures). Image order set by content team in admin.
      2. selectedVariant.image — single featured photo if no var_images.
      3. product.images[] — full product gallery as last-resort fallback.

    On variant switch, _selectVariant() calls this method again with the
    new variant — gallery rebuilds with that shade's media.

    URLs come pre-sized from Liquid at 1200w. The legacy `_x550` regex
    substitution was a no-op (Shopify CDN URLs don't have .ext in path)
    so it's removed.
  */
  _renderImages(product, selectedVariant) {
    const row = this.querySelector('[data-eisnova-overlay-images-row]');
    if (!row) return;
    row.innerHTML = '';

    let imgs = [];

    /* Priority 1: variant-specific media gallery */
    if (selectedVariant && Array.isArray(selectedVariant.media) && selectedVariant.media.length > 0) {
      imgs = selectedVariant.media;
    }
    /* Priority 2: variant featured image */
    else if (selectedVariant && selectedVariant.image) {
      imgs = [selectedVariant.image];
    }
    /* Priority 3: product gallery fallback */
    else if (Array.isArray(product.images) && product.images.length > 0) {
      imgs = product.images;
    }
    else if (product.featured_image) {
      imgs = [product.featured_image];
    }

    /*
      Loading veil: shade switches rebuild the row and images pop in
      one by one at width:auto, making the gallery visually "jump".
      We cover the gallery with a same-color veil (.is-loading) and lift
      it only after ALL media have loaded AND decoded — decode() matters
      because a loaded-but-undecoded image still paints late and jitters.

      - loading switched lazy → eager: lazy images outside the scroller's
        first viewport never fetch until scrolled, so "wait for all"
        would deadlock.
      - _imgLoadToken guards against race conditions on rapid shade
        switching: only the latest render is allowed to lift the veil.
      - 4s safety timeout: one stalled CDN request must never leave the
        gallery permanently covered.
    */
    const gallery = row.closest('.eisNova-overlay__images');
    const token = (this._imgLoadToken = (this._imgLoadToken || 0) + 1);
    const pending = [];

    imgs.forEach(src => {
      if (!src) return;
      const wrap = document.createElement('div');
      wrap.className = 'eisNova-overlay__imageItem';
      const img = document.createElement('img');
      img.loading = 'eager';
      img.decoding = 'async';
      img.src = src.startsWith('//') ? `https:${src}` : src;
      img.alt = product.title || '';
      pending.push(
        img.decode
          ? img.decode().catch(() => {}) /* decode() rejects on broken images — treat as settled */
          : new Promise(res => { img.onload = img.onerror = res; })
      );
      wrap.appendChild(img);
      row.appendChild(wrap);
    });

    if (gallery) {
      if (pending.length === 0) {
        gallery.classList.remove('is-loading');
        return;
      }
      gallery.classList.add('is-loading');
      const lift = () => {
        /* Ignore stale completions from a previous shade's render pass. */
        if (this._imgLoadToken === token) gallery.classList.remove('is-loading');
      };
      Promise.allSettled(pending).then(lift);
      setTimeout(lift, 4000);
    }
  }

  /*
    Renders variation pickers — TWO MODES, decided per-product:
    
    A) MAKEUP (any variant has metafield global.color-rgb) →
       circular color swatches. Uses background-color from metafield,
       title attribute for tooltip, selected variant title shown below.
    
    B) PERFUME / OTHER (no color-rgb on any variant) →
       text labels (volume / size). Same layout as before.
    
    Metafields are NOT exposed by Shopify's /products/{handle}.js endpoint.
    We pre-render them at Liquid time into a <script type="application/json"
    data-eisnova-variant-meta="{handle}"> tag adjacent to the trigger button.
    This method merges that metadata onto the product.variants array.
  */
  /*
    Renders variation pickers — TWO MODES per-product:
    
    A) MAKEUP (any variant has variant.colorRgb) →
       circular color swatches filled inline by JS.
       Tooltip = variant title, selected title shown below swatches.
    
    B) PERFUME / OTHER (no colorRgb) →
       text labels (volume / size).
    
    Variant data — including colorRgb — comes pre-resolved from the
    Liquid payload (see open() docs), NOT from a separate metaScript
    query. This is simpler than the previous two-source merge.
  */
  _renderVariations(product) {
    const list = this.querySelector('[data-eisnova-overlay-variations]');
    if (!list) return;
    list.innerHTML = '';

    /* Single default variant → hide block, nothing to pick. */
    if (product.variants.length === 1 && product.variants[0].title === 'Default Title') {
      list.hidden = true;
      this._setText('variationTitle', '');
      return;
    }
    list.hidden = false;

    /*
      MAKEUP detection — any variant carries a usable colorRgb (after
      normalization). We normalize ONCE per variant and cache to avoid
      re-parsing in the inner loop. Variant without a parseable color
      gets a transparent swatch so layout stays stable.
    */
    const colorByVariantId = {};
    product.variants.forEach(v => {
      colorByVariantId[v.id] = this._normalizeColor(v.colorRgb);
    });
    const hasColors = Object.values(colorByVariantId).some(c => c);

    list.classList.toggle('eisNova-overlay__variations--colors', hasColors);
    list.classList.toggle('eisNova-overlay__variations--text', !hasColors);

    const headingText = product.optionName
      || (typeof product.options?.[0] === 'string' ? product.options[0] : null)
      || (hasColors ? 'Shade' : 'Choose variant');
    this._setText('variationTitle', headingText);

    product.variants.forEach(v => {
      const li = document.createElement('li');
      li.className = 'eisNova-overlay__variation';
      li.setAttribute('data-eisnova-variant-id', String(v.id));
      li.setAttribute('role', 'radio');
      li.setAttribute('aria-checked', 'false');
      li.setAttribute('tabindex', '0');
      li.setAttribute('title', v.title);
      if (!v.available) li.classList.add('eisNova-overlay__variation--unavailable');

      const variantColor = colorByVariantId[v.id];
      if (hasColors && variantColor) {
        li.classList.add('eisNova-overlay__variation--color');
        li.style.backgroundColor = variantColor;
        const sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = v.title;
        li.appendChild(sr);
      } else {
        li.classList.add('eisNova-overlay__variation--text');
        li.textContent = v.title;
      }
      list.appendChild(li);
    });
  }

  /*
    Variant selection — also swaps gallery image to the new variant's
    featured photo (matches PDP behavior where shade switch updates the
    main product image). Pack-size text under title is updated too.
  */
  _selectVariant(variantId) {
    const v = this._product?.variants.find(v => String(v.id) === String(variantId));
    if (!v) return;
        /*
      Sold-out shades are selectable on purpose. The previous early return made
      a tap on a greyed swatch do nothing at all, which reads as a broken page:
      the customer cannot tell whether the shade is unavailable or whether the
      site failed. Selection now goes through, the gallery and price follow it,
      and _syncSelectedVariantUI turns the add button into the sold-out state.
      The add itself is blocked there and in _submitAdd.
    */
    this._selectedVariantId = String(variantId);
    this._setText('packSize', v.packSize || '');
    this._renderImages(this._product, v);
    this._syncSelectedVariantUI();
  }

  /*
    On variant select: highlight active swatch/label, update price, show
    selected variant title below color swatches (so users see e.g. "R01
    ROUGE ARDENT" under the colored circle they tapped).
  */
  /*
    On variant select: highlight active swatch, update price label, show
    selected variant title below swatches.

    Price prefers v.priceFormatted (from Liquid payload) over JS-formatted
    money. Liquid uses `shop.money_format` setting which respects locale,
    currency symbol, decimals — we'd need to duplicate that in JS otherwise.
    Fallback to legacy _formatMoney() for legacy /products.js payloads.
  */
  _syncSelectedVariantUI() {
    const v = this._product?.variants.find(v => String(v.id) === this._selectedVariantId);
    if (!v) return;

    this.querySelectorAll('.eisNova-overlay__variation').forEach(el => {
      const active = el.dataset.eisnovaVariantId === this._selectedVariantId;
      el.classList.toggle('eisNova-overlay__variation--active', active);
      el.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    /* Total price (unit × qty) — extracted because it must also update on
       qty +/- (see _syncQtyUI). Kept on its own method so a single source
       of truth formats the price label. */
    this._renderTotalPrice();

    this._setText('variantTitleSelected', v.title);

    this._syncAvailabilityUI(v);
  }


  /*
    Add button state for the selected variant.

    Labels are read from window.variantStrings, which layout/theme.liquid fills
    from the locale files. They are therefore already translated for the market
    that rendered the page; a literal 'Sold out' here would print English on
    every non-English storefront.
  */
  _syncAvailabilityUI(v) {
    const submit = this.querySelector('[data-eisnova-overlay-submit]');
    if (!submit) return;


    const strings = window.variantStrings || {};
    const label = this.querySelector('[data-eisnova-overlay-submit-label]');


    submit.disabled = !v.available;
    submit.classList.toggle('eisNova-overlay__addBtn--sold-out', !v.available);
    if (label) {
      label.textContent = v.available
        ? (strings.addToCart || 'Add to cart')
        : (strings.soldOut || 'Sold out');
    }
  }

  /*
    Renders the price label as (variant.price * this._qty), formatted.

    Strategy:
      - qty === 1 → prefer Liquid's priceFormatted ("44,50 €") to preserve
        exact locale styling (decimal separator, currency symbol/position).
      - qty > 1 → multiply cents and run through _formatMoney(), which
        delegates to window.Shopify.formatMoney with theme.moneyFormat when
        present (preserves locale), otherwise a generic fallback.

    Called from:
      - _syncSelectedVariantUI() on initial render and variant switch
      - _syncQtyUI() on every +/- click and direct input change
  */
  _renderTotalPrice() {
    const v = this._product?.variants.find(v => String(v.id) === this._selectedVariantId);
    if (!v) return;

    if (this._qty === 1 && v.priceFormatted) {
      this._setText('price', v.priceFormatted);
      return;
    }

    const totalCents = (v.price || 0) * this._qty;
    this._setText('price', this._formatMoney(totalCents));
  }

  _changeQty(delta) {
    this._setQty(this._qty + delta);
  }

  _setQty(val) {
    if (isNaN(val) || val < 1) val = 1;
    if (val > this._MAX_QTY) val = this._MAX_QTY;
    this._qty = val;
    this._syncQtyUI();
  }

  _syncQtyUI() {
    const input = this.querySelector('[data-eisnova-overlay-qty-input]');
    if (input && String(input.value) !== String(this._qty)) input.value = this._qty;
    const minusBtn = this.querySelector('[data-eisnova-overlay-qty-minus]');
    const plusBtn = this.querySelector('[data-eisnova-overlay-qty-plus]');
    if (minusBtn) minusBtn.disabled = this._qty <= 1;
    if (plusBtn) plusBtn.disabled = this._qty >= this._MAX_QTY;
    /* Price scales with qty — recompute on every qty mutation. */
    this._renderTotalPrice();
  }

  /* ---------- ADD TO CART ---------- */

  /*
    Submit "Add to cart" from overlay.

    LIMIT VALIDATION (server-authoritative double-check happens later in
    cart-drawer.js, but we enforce client-side first to avoid round-trips
    and to show the user-facing toast immediately):

      1. DISCOVERY SETS limit: if product has tag "DISCOVERY SETS", the
         maximum allowed quantity is 1 PER ORDER (not per add). We check
         the live cart for any line item whose product carries that tag
         and block the add if it'd push total > 1.

      2. Per-product 4-piece limit: independent of tags, no single
         product variant can exceed 4 in the cart. We sum existing
         qty for this variant + the qty user is trying to add.

    Both rules show a centered toast popup; user dismisses by clicking
    the toast or anywhere outside.
  */
  async _submitAdd() {
    if (!this._selectedVariantId) return;
    
    /* Availability gate. The button is disabled, but keyboard activation and
       future callers can still reach this method. */
    const selected = this._product?.variants.find(v => String(v.id) === this._selectedVariantId);
    if (selected && !selected.available) {
      const strings = window.variantStrings || {};
      EisNovaToast.show(strings.soldOut || 'Sold out');
      return;
    }
    this._setBusy(true);
    this._showError('');

    const drawer = document.querySelector('cart-drawer');
    const sectionIds = drawer ? drawer.getSectionsToRender().map(s => s.id) : ['cart-drawer', 'cart-icon-bubble'];

    try {
      /* Pre-check 1 + 2: fetch current cart to know existing quantities */
      const limitError = await this._validateCartLimits();
      if (limitError) {
        this._setBusy(false);
        EisNovaToast.show(limitError);
        return;
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(this._selectedVariantId), quantity: this._qty }],
          sections: sectionIds
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || `Add failed: ${res.status}`);
      }
      const response = await res.json();

      /* Piggyback: reuse drawer's own method to apply fresh section HTML,
         which also triggers the gift sync path via runGiftSync. */
      if (drawer && response.sections) {
        drawer.applySectionsFromMap(response.sections);
        const drawerHTML = response.sections['cart-drawer'];
        const preloadedConfig = drawerHTML
          ? drawer.extractConfigFromSectionHTML(drawerHTML)
          : null;
        await drawer.runGiftSync({ preloadedConfig });
      }

      /* Broadcast for GA4 / analytics integrators to pick up without
         tangling tracking into the drawer DOM. */
      document.dispatchEvent(new CustomEvent('eisNova:item-added', {
        detail: { product: this._product, variantId: this._selectedVariantId, quantity: this._qty }
      }));

      this.close();
    } catch (err) {
      console.error('[eisNova-overlay] add failed:', err);
      this._showError(err.message || 'Add to cart failed.');
    } finally {
      this._setBusy(false);
    }
  }

  /*
    Per-product cap validation. DS-tagged products use cap=1, everything
    else uses cap=4. Logic is the same in both cases — we just use a
    different limit number depending on whether the current product carries
    the "discovery sets" tag.

    Important: each DS product is independently capped at 1. Customers can
    have multiple DIFFERENT discovery sets in cart (1 each), they just
    can't stockpile multiples of the same one. Quantity is summed across
    all cart lines for the same product_id.

    Returns user-facing error string if the requested add would violate
    the cap. Returns null if everything is OK.
  */
  async _validateCartLimits() {
    if (!this._product) return null;

    const tags = (this._product.tags || []).map(t => String(t).toLowerCase().trim());
    const isDiscoverySet = tags.includes('discovery sets');
    const cap = isDiscoverySet ? 1 : 4;
    const productLabel = isDiscoverySet ? 'Discovery Set' : 'this item';

    /* Fast fail: requested qty alone exceeds cap */
    if (this._qty > cap) {
      return `Sorry, you cannot add more than ${cap} of ${productLabel} per order.`;
    }

    /* Fetch current cart state to count existing units of THIS product */
    let cart;
    try {
      cart = await fetch('/cart.js').then(r => r.json());
    } catch (err) {
      return null; /* let server enforce */
    }

    /* Cap per VARIANT for regular products (a different shade adds on its own,
       matching the product page + cart drawer). Discovery Sets stay product-
       level on purpose — it's one box regardless of which variant is picked. */
    const productId = Number(this._product.id);
    const targetVariantId = Number(this._selectedVariantId);
    const currentQty = (cart.items || [])
      .filter(item => isDiscoverySet
        ? Number(item.product_id) === productId
        : Number(item.variant_id) === targetVariantId)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);

    if (currentQty + this._qty > cap) {
      const remaining = Math.max(0, cap - currentQty);
      if (remaining === 0) {
        return `Sorry, you cannot add more than ${cap} of ${productLabel} per order.`;
      }
      return `Sorry, you can only add ${remaining} more of ${productLabel} (limit: ${cap} per order).`;
    }

    return null;
  }

  _setBusy(busy) {
    const btn = this.querySelector('[data-eisnova-overlay-submit]');
    const spinner = this.querySelector('[data-eisnova-overlay-submit-spinner]');
    if (btn) {
      /*
        Releasing the busy state used to enable the button unconditionally. On
        a sold-out variant that turned a dead button live again after any
        failed or aborted add, so the availability state has to be re-applied
        rather than assumed.
      */
      const v = this._product?.variants.find(x => String(x.id) === this._selectedVariantId);
      btn.disabled = busy || (v ? !v.available : false);
      btn.toggleAttribute('data-eisnova-overlay-busy', busy);
    }
    if (spinner) spinner.hidden = !busy;
  }

  _showError(msg) {
    const el = this.querySelector('[data-eisnova-overlay-error]');
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
  }

  /* ---------- DOM HELPERS ---------- */

  _setText(field, value) {
    const el = this.querySelector(`[data-eisnova-field="${field}"]`);
    if (el) el.textContent = value || '';
  }

  _setHref(field, url) {
    const el = this.querySelector(`[data-eisnova-field="${field}"]`);
    if (el && 'href' in el) el.href = url || '';
  }

  _plainDescription(html) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = (tmp.textContent || '').trim();
    return text.length > 220 ? text.slice(0, 217) + '…' : text;
  }

  /*
    Format `cents` into a localized money string. Three-tier strategy:

    1. Shopify.formatMoney with theme.moneyFormat — preferred when theme.liquid
       exposes shop.money_format (set up in layout/theme.liquid). Produces
       byte-identical output to Liquid's `| money` filter.
    2. Intl.NumberFormat with Shopify.currency.active — robust fallback that
       still honors locale (decimal sep, currency symbol position). On a EUR
       store with a French browser → "89,00 €".
    3. Plain "amount + currency code" — last resort if neither API is exposed.

    Why no '${{amount}}' fallback anymore: that string forces USD-styled output
    on EUR/GBP/etc. stores. Bug surfaced as price flipping to "$89.00" when
    qty>1 in the overlay.
  */
  _formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function'
        && window.theme && typeof window.theme.moneyFormat === 'string') {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
    if (currency && typeof Intl !== 'undefined' && typeof Intl.NumberFormat === 'function') {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency
        }).format(cents / 100);
      } catch (_) { /* fall through */ }
    }
    const val = (cents / 100).toFixed(2);
    return currency ? `${val} ${currency}` : val;
  }
}


/* =========================================
   SECTION: EisNovaToast — centered popup notification
   
   Lightweight singleton — one toast at a time. Auto-dismiss after 4s,
   or on click (anywhere on the toast or backdrop).
   
   Used by overlay's _validateCartLimits() and by main-product.liquid's
   discovery-set guards. Replaces the old plain window.alert() that we
   put in as a placeholder during Phase 2.
   
   No external dependencies — pure DOM manipulation. Renders a backdrop
   + centered card with message + tiny close affordance.
========================================= */
const EisNovaToast = {
  _el: null,
  _timer: null,

  show(message) {
    this._ensureElement();
    this._el.querySelector('.eisNova-toast__message').textContent = message;
    this._el.classList.add('eisNova-toast--visible');
    document.body.classList.add('eisNova-toast--open');

    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.hide(), 4000);
  },

  hide() {
    if (!this._el) return;
    this._el.classList.remove('eisNova-toast--visible');
    document.body.classList.remove('eisNova-toast--open');
    clearTimeout(this._timer);
  },

  _ensureElement() {
    if (this._el) return;
    const wrap = document.createElement('div');
    wrap.className = 'eisNova-toast';
    wrap.innerHTML = `
      <div class="eisNova-toast__backdrop" data-eisnova-toast-close></div>
      <div class="eisNova-toast__card" role="alert" aria-live="assertive">
        <p class="eisNova-toast__message"></p>
        <button type="button" class="eisNova-toast__close" data-eisnova-toast-close aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M11 3L3 11M3 3l8 8" stroke="currentColor" stroke-width="1.5"/>
          </svg>
        </button>
      </div>
    `;
    document.body.appendChild(wrap);
    this._el = wrap;

    wrap.addEventListener('click', (e) => {
      if (e.target.closest('[data-eisnova-toast-close]')) {
        this.hide();
      }
    });
  }
};

/* Expose globally so main-product.liquid (and any future use sites) can
   call EisNovaToast.show(...) without importing modules. */
window.EisNovaToast = EisNovaToast;

customElements.define('eisnova-product-overlay', EisNovaProductOverlay);


/* =========================================
   Global Delegations
   Replaces all the inline onclick="" handlers from the previous version.

   CAPTURE PHASE LISTENER for overlay/direct-add triggers:
   Third-party apps like Candy Rack install document-level click listeners
   on ALL "add to cart" buttons in the bubble phase, intercepting them
   before our handler runs. Their handler then crashes (it can't find a
   variant_id on our overlay-trigger buttons since those have only a
   product_handle), which kills event propagation entirely — our handler
   never fires and the overlay never opens.

   Solution: register THIS listener in the capture phase (third arg = true)
   so we run BEFORE any document-level bubble listener, and call
   stopImmediatePropagation() to prevent Candy Rack from seeing the click
   at all. Their app remains functional for cards we don't intercept
   (single-variant catalog products with type=submit, etc.).

   Other delegations (close drawer, toggle, discount form) stay in the
   second listener below — they don't conflict with third-party apps.
========================================= */

document.addEventListener('click', (e) => {
  /*
    Open overlay — multi-variant catalog/cross-sell/routine cards.
    
    UX flow: when triggered from a page outside the drawer (catalog grid,
    bestsellers carousel), we want BOTH the drawer to slide in AND the
    overlay to appear over its content. The drawer.open() call drops
    the visibility:hidden state on the .drawer parent, which in turn
    makes our overlay (rendered as a child) visible.
    
    When triggered from INSIDE an already-open drawer (cross-sell sidebar
    QUICK SHOP), drawer.open() is idempotent and just keeps state.
  */
  const overlayTrigger = e.target.closest('[data-eisnova-overlay-open]');
  if (overlayTrigger) {
    e.preventDefault();
    e.stopImmediatePropagation();
    const handle = overlayTrigger.dataset.eisnovaProductHandle;
    const drawer = document.querySelector('cart-drawer');
    const overlay = document.querySelector('eisnova-product-overlay');
    if (!overlay) return;
    /* Open drawer first so visibility cascades to overlay child;
       overlay opens immediately after — no wait needed since CSS
       visibility transition is instant once .active class is set. */
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
    overlay.open(handle);
    return;
  }

  /* Direct add (single-variant cross-sell / routine — bypass overlay) */
  const addTrigger = e.target.closest('[data-eisnova-add-variant]');
  if (addTrigger) {
    e.preventDefault();
    e.stopImmediatePropagation();
    const id = addTrigger.dataset.variantId;
    if (!id) return;
    _directAdd(addTrigger, Number(id));
    return;
  }
}, true); /* ← CAPTURE PHASE — runs before bubble listeners (Candy Rack) */

/*
  Secondary delegation for non-cart-add triggers — stays in default
  bubble phase since these don't conflict with third-party widgets.
*/
document.addEventListener('click', (e) => {

  /* Close cart drawer */
  if (e.target.closest('[data-eisnova-close-drawer]')) {
    e.preventDefault();
    document.querySelector('cart-drawer')?.close();
    return;
  }

  /* Cross-sell panel toggle / close */
  const toggleTrigger = e.target.closest('[data-eisnova-toggle]');
  if (toggleTrigger) {
    const target = document.querySelector(toggleTrigger.dataset.eisnovaToggle);
    if (target) target.classList.toggle('is-open');
    return;
  }
  const closeTrigger = e.target.closest('[data-eisnova-close]');
  if (closeTrigger) {
    const target = document.querySelector(closeTrigger.dataset.eisnovaClose);
    if (target) target.classList.remove('is-open');
    return;
  }

  /*
    Discount code apply.

    Behavior split by outcome:
      success → applyDiscount() refreshed the cart drawer's HTML, so input/errEl
                refs in this closure now point to detached DOM nodes. Re-query
                the FRESH DOM, clear the input, and force the <details>
                accordion back to open=true (it auto-closed because the new
                HTML rendered <details> without the open attribute).

      failure → no refresh happened, original DOM intact. Use the captured
                refs to set the error message — accordion stays open with the
                user's typed code still visible for them to correct.
  */
  if (e.target.closest('[data-discount-submit]')) {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('[data-discount-input]');
    const errEl = form.querySelector('[data-discount-error]');
    const submitBtn = e.target.closest('[data-discount-submit]');

    const setError = (msg) => {
      if (!errEl) return;
      if (msg) {
        errEl.textContent = msg;
        errEl.style.display = 'block';
      } else {
        errEl.textContent = '';
        errEl.style.display = 'none';
      }
    };

    const codeValue = input?.value || '';
    if (!codeValue.trim()) {
      setError('Please enter a code');
      return;
    }
    setError('');
    if (submitBtn) submitBtn.disabled = true;

    document.querySelector('cart-drawer')?.applyDiscount(codeValue).then(r => {
      if (r?.success) {
        /* Post-refresh: original input/errEl are detached. Operate on the
           freshly rendered nodes via global queries. */
        const freshInput = document.querySelector('[data-discount-input]');
        const freshErr = document.querySelector('[data-discount-error]');
        const freshDetails = document.querySelector('[data-discount-form]')?.closest('details');
        if (freshInput) freshInput.value = '';
        if (freshErr) {
          freshErr.textContent = '';
          freshErr.style.display = 'none';
        }
        /* Pop the accordion back open so user sees the applied-list confirmation
           rendered inside it. Without this they'd just see a closed strip and
           wonder if anything happened — they'd have to scroll/expand again. */
        if (freshDetails) freshDetails.open = true;
      } else {
        setError(r?.message || 'Invalid discount code');
      }
    }).finally(() => {
      /* submitBtn might also be detached after success refresh — re-find it
         globally to safely clear disabled state regardless of path. */
      const stillBtn = document.querySelector('[data-discount-submit]') || submitBtn;
      if (stillBtn) stillBtn.disabled = false;
    });
    return;
  }
});

/**
 * Direct-add helper — adds a single variant to cart with sections piggyback
 * so drawer re-renders without an extra refresh. Used by "ADD" buttons on
 * routine / cross-sell cards that have a single variant and don't need
 * the overlay variant picker.
 */
/*
  Direct-add helper — adds a single variant to cart with sections piggyback.
  Used by single-variant cards in cross-sell/routine where there's no
  variant picker (just an "ADD" button with data-variant-id).

  Limit validation runs BEFORE /cart/add to keep UX consistent with the
  multi-variant overlay path (_validateCartLimits) and the cart-drawer
  +/- path (_validateDrawerLimit). Without this, the cross-sell sidebar
  becomes a backdoor that bypasses the 4-cap and DS=1 rules.

  Trigger button's parent card is expected to expose product metadata via
  data-eisnova-product-handle so we can read the pre-rendered Liquid
  payload (same JSON used by the multi-variant overlay) and run the
  per-product cap check from the same source of truth.
*/
async function _directAdd(triggerEl, variantId) {
  const originalLabel = triggerEl.textContent;
  const drawer = document.querySelector('cart-drawer');
  const sectionIds = drawer ? drawer.getSectionsToRender().map(s => s.id) : ['cart-drawer', 'cart-icon-bubble'];

  /* Pre-flight limit check */
  try {
    const limitError = await _validateDirectAddLimits(triggerEl, variantId);
    if (limitError) {
      if (window.EisNovaToast) {
        window.EisNovaToast.show(limitError);
      }
      return; /* abort: do not fire /cart/add */
    }
  } catch (err) {
    /* If validation itself fails (network), fall through and let
       Shopify enforce server-side. We don't want a transient cart.js
       error to block the add. */
    console.warn('[eisNova] _directAdd validation failed, proceeding:', err);
  }

  triggerEl.disabled = true;
  triggerEl.textContent = '...';
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds
      })
    });
    if (!res.ok) throw new Error(`Add failed: ${res.status}`);
    const response = await res.json();

    if (drawer && response.sections) {
      drawer.applySectionsFromMap(response.sections);
      const drawerHTML = response.sections['cart-drawer'];
      const preloadedConfig = drawerHTML
        ? drawer.extractConfigFromSectionHTML(drawerHTML)
        : null;
      await drawer.runGiftSync({ preloadedConfig });
    }

    document.dispatchEvent(new CustomEvent('eisNova:item-added', {
      detail: { variantId, quantity: 1 }
    }));
  } catch (err) {
    console.error('[eisNova] direct add failed:', err);
    triggerEl.textContent = originalLabel;
  } finally {
    /* triggerEl may have been destroyed by applySectionsFromMap re-rendering
       the cart-drawer section. Only restore if it's still in the DOM. */
    if (document.contains(triggerEl)) {
      triggerEl.disabled = false;
      triggerEl.textContent = originalLabel;
    }
  }
}

/*
  Validates a "direct add" against per-product cart caps.

  Resolution strategy for product metadata:
    1. Trigger button's `data-eisnova-product-handle` (preferred — same
       attribute the overlay path uses, so single-variant and multi-variant
       cards share metadata).
    2. Find the product by walking the trigger's siblings looking for the
       <script data-eisnova-overlay-product> tag (some templates only put
       the data attribute on the multi-variant variant button).
    3. Skip validation if neither found — Shopify will enforce its own
       inventory rules and we degrade gracefully.

  Tags lookup: from the resolved product's `tags` array. Discovery Sets
  get cap=1, everything else cap=4. Logic is identical to overlay's
  _validateCartLimits — kept duplicated here because the overlay class
  scope makes sharing awkward without a global helper.
*/
async function _validateDirectAddLimits(triggerEl, variantId) {
  /* Try to find product payload near the trigger */
  const handle = triggerEl?.dataset?.eisnovaProductHandle;
  let productPayload = null;

  if (handle) {
    const payloadEl = document.querySelector(
      `script[data-eisnova-overlay-product="${handle}"]`
    );
    if (payloadEl) {
      try { productPayload = JSON.parse(payloadEl.textContent); } catch (_) {}
    }
  }

  /* Detect Discovery Set status. If no tags found, treat as regular product. */
  const tags = (productPayload?.tags || []).map(t => String(t).toLowerCase().trim());
  const isDiscoverySet = tags.includes('discovery sets');
  const cap = isDiscoverySet ? 1 : 4;
  const productLabel = isDiscoverySet ? 'Discovery Set' : 'this item';

  /* Read live cart to count existing units of the target product/variant */
  let cart;
  try {
    cart = await fetch('/cart.js').then(r => r.json());
  } catch (err) {
    return null; /* let server enforce */
  }

  /* Cap per VARIANT (per shade) for regular products; Discovery Sets stay
     product-level (one box regardless of variant). Falls back to variant_id
     when no product payload is available. */
  const productId = productPayload?.id ? Number(productPayload.id) : null;
  const targetVid = Number(variantId);

  const currentQty = (cart.items || [])
    .filter(item => (isDiscoverySet && productId)
      ? Number(item.product_id) === productId
      : Number(item.variant_id) === targetVid)
    .reduce((sum, item) => sum + (item.quantity || 0), 0);

  if (currentQty + 1 > cap) {
    const remaining = Math.max(0, cap - currentQty);
    if (remaining === 0) {
      return `Sorry, you cannot add more than ${cap} of ${productLabel} per order.`;
    }
    return `Sorry, you can only add ${remaining} more of ${productLabel} (limit: ${cap} per order).`;
  }

  return null;
}

/*
  =========================================
  Catalog Card Click Area Guard — JS-based fix
  =========================================
  
  Problem: catalog product cards have <a class="catalog-product__media">
  wrapping the product image, AND our ADD TO CART button positioned below.
  On some viewport sizes, the wrapping <a> bounding box can stretch over
  the button area. Click then hits the link → redirect to PDP, overlay
  never opens. (Confirmed by elementFromPoint() returning <a> instead of
  <button> on test runs.)

  Why JS instead of CSS: This theme has had Shopify CDN serving stale
  CSS files. JS runs immediately on every page load regardless of CSS
  cache state. Inline styles also have higher specificity than any
  external rule, so we don't fight third-party app stylesheets.

  Strategy:
    1. Find every overlay-trigger and direct-add button on the page.
    2. For each, set inline position:relative + z-index:10 so it owns
       its own stacking context and sits above any sibling.
    3. Find the closest catalog card wrapper, set pointer-events:none
       on all <img> children (clicks pass through them, image stays
       visible — pointer-events doesn't affect paint), and lower the
       z-index of any sibling <a> that doesn't contain the button.

  Re-runs on:
    - DOMContentLoaded (initial render)
    - eisNova:item-added event (drawer cross-sell cards swap in/out)
    - MutationObserver (carousels lazy-render slides as user navigates)
*/
function eisNovaArmCatalogCards() {
  const triggers = document.querySelectorAll(
    '[data-eisnova-overlay-open], [data-eisnova-add-variant]'
  );
  triggers.forEach((btn) => {
    /* Lift button above siblings with inline styles (highest specificity) */
    btn.style.position = 'relative';
    btn.style.zIndex = '10';
    btn.style.pointerEvents = 'auto';

    /* Resolve catalog wrapper — works for legacy Eisenberg .catalog-product
       and our drawer-internal cross-sell/routine cards. */
    const card = btn.closest(
      '.catalog-product, .eisNova-crossPanel__card, .eisNova-routine__card'
    );
    if (!card) return;

    /* Make all images click-transparent. Image stays visible — pointer-events
       only affects hit-testing, not painting. */
    card.querySelectorAll('img').forEach((img) => {
      img.style.pointerEvents = 'none';
    });

    /* Lower wrapping <a> tags below the button's stacking. They retain
       click-through on non-button areas, so clicking the image area
       still navigates to PDP — that's intended. */
    card.querySelectorAll('a').forEach((link) => {
      if (!link.contains(btn)) {
        link.style.position = link.style.position || 'relative';
        link.style.zIndex = '1';
      }
    });
  });
}

/* Initial arm */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', eisNovaArmCatalogCards);
} else {
  eisNovaArmCatalogCards();
}

/* Re-arm after cart drawer re-renders (cross-sell cards swap in/out) */
document.addEventListener('eisNova:item-added', () => {
  setTimeout(eisNovaArmCatalogCards, 100);
});

/* Re-arm when carousels lazy-render new slides via MutationObserver */
if (typeof MutationObserver !== 'undefined') {
  const cardObserver = new MutationObserver((mutations) => {
    let shouldRearm = false;
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (
          node.matches?.('[data-eisnova-overlay-open], [data-eisnova-add-variant]') ||
          node.querySelector?.('[data-eisnova-overlay-open], [data-eisnova-add-variant]')
        ) {
          shouldRearm = true;
          break;
        }
      }
      if (shouldRearm) break;
    }
    if (shouldRearm) {
      /* Throttle: don't rearm more than once per 100ms */
      clearTimeout(eisNovaArmCatalogCards._t);
      eisNovaArmCatalogCards._t = setTimeout(eisNovaArmCatalogCards, 100);
    }
  });
  cardObserver.observe(document.body, { childList: true, subtree: true });
}
